<template>
  <li
    class="p-contentplans-writing-serps-headings-item"
    :data-level="item.type"
  >
    <div class="p-contentplans-writing-serps-headings-item__content">
      <div class="p-contentplans-writing-serps-headings-item__level">
        <HeadingType :level="item.type" />
      </div>
      <div class="p-contentplans-writing-serps-headings-item__text">
        <div v-if="isEmptyText">テキストが設定されていません。</div>
        <div v-else>
          <template v-if="isOverTextMaxLength">
            {{ item.text.substr(0, MAX_TDH_STRING_LENGTH) }}...<span
              class="c-text--neutral"
              >{{
                `(※${MAX_TDH_STRING_LENGTH + 1}文字以上は表示しません)`
              }}</span
            >
          </template>
          <template v-else>
            {{ item.text }}
          </template>
          <div
            v-if="canAddHeading"
            class="p-contentplans-writing-serps-headings-item__action"
          >
            <button
              type="button"
              class="p-contentplans-writing-serps-headings-item__add"
              ref="button"
              @click="addHeading"
              @mouseenter="setTooltipPosition"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="1"
                  y="1"
                  width="18"
                  height="18"
                  rx="2"
                  fill="#EEF4F9"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M9.25 14.875C9.25 15.0821 9.41789 15.25 9.625 15.25H10.375C10.5821 15.25 10.75 15.0821 10.75 14.875V10.75H14.875C15.0821 10.75 15.25 10.5821 15.25 10.375V9.625C15.25 9.41789 15.0821 9.25 14.875 9.25H10.75V5.125C10.75 4.91789 10.5821 4.75 10.375 4.75H9.625C9.41789 4.75 9.25 4.91789 9.25 5.125V9.25H5.125C4.91789 9.25 4.75 9.41789 4.75 9.625V10.375C4.75 10.5821 4.91789 10.75 5.125 10.75H9.25V14.875Z"
                  fill="#8994AC"
                />
              </svg>
              <span v-show="!error" class="tooltip" ref="tooltip"
                >構成案に追加<span class="arrow" ref="tooltipArrow"></span
              ></span>
            </button>
            <span v-if="error" class="tooltip tooltip--alert" ref="alert"
              >{{ error }}<span class="arrow" ref="alertArrow"></span
            ></span>
          </div>
        </div>
      </div>
    </div>
    <ul v-if="hasChildren" class="p-contentplans-writing-serps-headings-list">
      <SerpsTitlesListItemHeadings
        v-for="(header, index) in item.children"
        :key="index"
        :errorAddHeading="errorAddHeading"
        :item="header"
        :isEditMode="isEditMode"
        @addHeading="addChildrenHeading"
      />
    </ul>
  </li>
</template>
<script>
import HeadingType from '@/components/HeadingType';
import SerpsTitlesListItemHeadings from '@/components/contentPlans/writing/SerpsTitlesListItemHeadings';
import { MAX_TDH_STRING_LENGTH } from '@/constants';
import {
  computePosition,
  arrow,
  autoPlacement,
  offset,
  size
} from '@floating-ui/dom';

export default {
  // 再起的に呼び出すため、name属性を設定
  name: 'SerpsTitlesListItemHeadings',
  props: ['errorAddHeading', 'item', 'isEditMode'],
  components: {
    HeadingType,
    SerpsTitlesListItemHeadings
  },
  data() {
    return {
      error: '',
      timeoutId: null
    };
  },
  computed: {
    isEmptyText() {
      // 必要なプロパティがない場合は一旦falseを返す (returnで判定を1回にできるが条件式が複雑になるため)
      if (!this.item.isImage && !this.item.text) {
        return true;
      }
      // 画像あり、テキストなしの場合は空文字で表示するので判定はfalseを返す
      if (this.item.isImage && !this.item.text) {
        return false;
      }
      // 画像要素ではない場合、空値ではなくても改行のみとかの場合は空テキストと判定
      return !!(
        this.item.text.replace(/\r?\n/g, '') === '' &&
        this.item.isImage === false
      );
    },
    canAddHeading() {
      return (
        this.isEditMode &&
        !this.isEmptyText &&
        this.item.text &&
        /h[2-4]/.test(this.item.type)
      );
    },
    hasChildren() {
      return !!(
        this.item.children &&
        this.item.children.length > 0 &&
        this.item.children[0].type !== undefined
      );
    },
    isOverTextMaxLength() {
      if (this.item.text) {
        return this.item.text.length > MAX_TDH_STRING_LENGTH;
      }
      return false;
    },
    MAX_TDH_STRING_LENGTH() {
      return MAX_TDH_STRING_LENGTH;
    }
  },
  watch: {
    errorAddHeading: {
      handler(value) {
        if (!value) {
          // 見出しが追加できるようになったらエラーメッセージを消す
          this.clearError();
        }
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this.timeoutId);
  },
  methods: {
    addHeading() {
      if (this.errorAddHeading) {
        this.clearError();
        this.error = this.errorAddHeading;
        this.setAlertPosition();

        this.timeoutId = setTimeout(() => {
          this.error = '';
        }, 2000);
        return;
      }
      // 空白や改行コードが含まれる場合があるので除去する
      const text = this.item.text.trim();
      this.$emit('addHeading', this.item.type, text);
    },
    addChildrenHeading(level, value) {
      this.$emit('addHeading', level, value.substr(0, MAX_TDH_STRING_LENGTH));
    },
    clearError() {
      clearTimeout(this.timeoutId);
      this.error = '';
    },
    setPosition(targetEl, tooltipEl, arrowEl) {
      if (!targetEl || !tooltipEl || !arrowEl) {
        return;
      }
      computePosition(targetEl, tooltipEl, {
        placement: 'top',
        middleware: [
          arrow({ element: arrowEl }),
          autoPlacement({
            allowedPlacements: ['top', 'top-start', 'top-end']
          }),
          offset(5),
          size({
            apply({ availableWidth, elements }) {
              Object.assign(elements.floating.style, {
                maxWidth: `${availableWidth}px`
              });
            }
          })
        ]
      }).then(({ x, y, middlewareData }) => {
        Object.assign(tooltipEl.style, { left: `${x}px`, top: `${y}px` });

        if (middlewareData.arrow) {
          const { x, y } = middlewareData.arrow;
          Object.assign(arrowEl.style, {
            left: x != null ? `${x}px` : '',
            top: y != null ? `${y}px` : ''
          });
        }
      });
    },
    setAlertPosition() {
      this.$nextTick(() => {
        const buttonEl = this.$refs.button;
        const tooltipEl = this.$refs.alert;
        const arrowEl = this.$refs.alertArrow;
        this.setPosition(buttonEl, tooltipEl, arrowEl);
      });
    },
    setTooltipPosition() {
      this.$nextTick(() => {
        const buttonEl = this.$refs.button;
        const tooltipEl = this.$refs.tooltip;
        const arrowEl = this.$refs.tooltipArrow;
        this.setPosition(buttonEl, tooltipEl, arrowEl);
      });
    }
  }
};
</script>
